import { lowerCase } from 'lodash';

import {
  ASSET_TYPE,
  type AssetType,
  isAssetDisabledAnnotations,
  isImageAssetType,
} from 'src/data/asset';
import { createEnumClampFunction } from 'src/lib/enum';
import type { Maybe } from 'src/lib/types';

import {
  BLANK_SAVED_PROMPT_ID,
  DEFAULT_GENERATION_TOOL,
  GENERATION_TOOL,
  GENERATION_TOOL_BADGE_TYPE,
  GENERATION_TOOL_BASIC,
  GENERATION_TOOL_DD,
  GENERATION_TOOL_DD_TEADS,
  GENERATION_TOOL_DEPRECATED,
  GENERATION_TOOL_FREESTYLE,
  GENERATION_TOOL_GENERAL_TEXT,
  GENERATION_TOOL_LPCO,
  GENERATION_TOOL_REWRITE,
  GENERATION_TOOLS_INCLUDING_SUB_TEMPLATES_IN_GROUP,
  type GenerationTool,
  type GenerationToolBadgeType,
  type GenerationToolBasic,
  type GenerationToolDDTeads,
  type GenerationToolDeprecated,
  type GenerationToolGeneralText,
  type GenerationToolLPCO,
  type GenerationToolRewrite,
  TEXT_UNSCORED_LABEL,
} from './const';
import {
  doesToolSupportSuggestKeywords,
  getToolContentType,
  getToolDefaultAssetType,
  getToolDescription,
  getToolLabel,
  isNoScoreTool,
} from './toolProps';

export const isGenerationTool = (tool: Maybe<string>): tool is GenerationTool =>
  Object.values(GENERATION_TOOL).includes(tool as GenerationTool);

export const isBasicGenerationTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_BASIC).includes(tool as GenerationToolBasic);

export const isLpcoGenerationTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_LPCO).includes(tool as GenerationToolLPCO);

export const isDataDrivenGenerationTool = (tool: GenerationTool): boolean => {
  const list: GenerationTool[] = [
    ...Object.values(GENERATION_TOOL_DD),
    ...Object.values(GENERATION_TOOL_REWRITE),
    ...Object.values(GENERATION_TOOL_DD_TEADS),
  ];
  return list.includes(tool);
};

// Blog tools the BE scores even though they live outside GENERATION_TOOL_DD —
// their BE names lack the `dd_` prefix because they originate from blog-post
// freestyle prompts but still have algo scoring configured (CSV registration).
const SCORABLE_BLOG_TOOLS: GenerationTool[] = [
  GENERATION_TOOL.blogDescription,
  GENERATION_TOOL.blogPostIntroParagraph,
  GENERATION_TOOL.blogPostSectionHeadline,
  GENERATION_TOOL.blogPostSectionSubHeadline,
];
export const isScorableBlogTool = (tool: GenerationTool): boolean =>
  SCORABLE_BLOG_TOOLS.includes(tool);

export const isLandingSeoMetaDataGenerationTool = (tool: GenerationTool): boolean =>
  tool === GENERATION_TOOL.landingSEOMetadata;

export const isSocialGenerationTool = (tool: GenerationTool): boolean => {
  const list: GenerationTool[] = [
    GENERATION_TOOL_DD.instagramCaption,
    GENERATION_TOOL_DD.facebookPost,
    GENERATION_TOOL_DD.tweet,
    GENERATION_TOOL_DD.linkedinPost,
    GENERATION_TOOL_DD.pinterestMixturePost,
    GENERATION_TOOL_DD.youtubeMixturePost,
    GENERATION_TOOL_DD.youtubeTitle,
    GENERATION_TOOL_DD.youtubeDescription,
    GENERATION_TOOL_DD.pinterestPostTitle,
    GENERATION_TOOL_DD.pinterestPostDescription,
  ];
  return list.includes(tool);
};

export const isEmailGenerationTool = (tool: GenerationTool): boolean => {
  const list: GenerationTool[] = [
    GENERATION_TOOL.emailSubject,
    GENERATION_TOOL.emailBody,
    GENERATION_TOOL.emailMixture,
  ];
  return list.includes(tool);
};

export const isAdsGenerationTool = (tool: GenerationTool): boolean => {
  const list: GenerationTool[] = [
    GENERATION_TOOL.linkedinMixtureAd,
    GENERATION_TOOL.twitterMixtureAd,
    GENERATION_TOOL.pinterestMixtureAd,
    GENERATION_TOOL.facebookMixtureAd,
    GENERATION_TOOL.googleRSA,
    GENERATION_TOOL.googlePerformanceMaxAd,
    GENERATION_TOOL.facebookPrimaryTextProduct,
    GENERATION_TOOL.facebookHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.outbrainHeadlineProduct,
    GENERATION_TOOL.taboolaHeadlineProduct,
    GENERATION_TOOL.twitterCopyProduct,
    GENERATION_TOOL.twitterTitleProduct,
    GENERATION_TOOL.pinterestDescriptionProduct,
    GENERATION_TOOL.pinterestTitleProduct,
    GENERATION_TOOL.googleAppAd,
    GENERATION_TOOL.googleDemandGenAd,
    GENERATION_TOOL.googleDisplayAd,
    GENERATION_TOOL.googleShoppingAd,
    GENERATION_TOOL.googleVideoAd,
    GENERATION_TOOL.outbrainAd,
    GENERATION_TOOL.taboolaAd,
  ];
  return list.includes(tool);
};

export const isTeadsGenerationTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_DD_TEADS).includes(tool as GenerationToolDDTeads);

export const isRewriteGenerationTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_REWRITE).includes(tool as GenerationToolRewrite);

/**
 * Using includes instead of === because freestyle tools are strings
 * in the following format 'freestyle_<tool_id>'
 */
export const isFreestyleGenerationTool = (tool: GenerationTool): boolean =>
  tool ? tool.startsWith(GENERATION_TOOL_FREESTYLE.freestyle) : false;

export const isCreateYourOwnTool = (tool: GenerationTool): boolean =>
  tool === GENERATION_TOOL_FREESTYLE.createYourOwn;

export const isCustomMultiPromptTool = (tool: GenerationTool): boolean =>
  tool === GENERATION_TOOL.customMultiPrompt;

/** True when the tool’s default asset is an image asset (covers all image template tools). */
export const isImageGenerationTool = (tool: GenerationTool): boolean => {
  if (!tool) {
    return false;
  }
  const assetType = getToolDefaultAssetType(tool);
  return assetType != null && isImageAssetType(assetType);
};

export const isTextGenerationTool = (tool: GenerationTool): boolean => !isImageGenerationTool(tool);

export const isTextDataDrivenGenerationTool = (tool: GenerationTool): boolean =>
  isDataDrivenGenerationTool(tool) && !isImageGenerationTool(tool);

/** True when the tool produces a picture and no copy beside it — `imageWithDescription` writes both. */
export const isImageOnlyGenerationTool = (tool: GenerationTool): boolean =>
  isImageGenerationTool(tool) && tool !== GENERATION_TOOL.imageWithDescription;

export const isSummarizerGenerationTool = (tool: GenerationTool): boolean =>
  tool === GENERATION_TOOL.summarizer;

export const isDeprecatedGenerationTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_DEPRECATED).includes(tool as GenerationToolDeprecated);

export const clampGenerationTool = createEnumClampFunction<GenerationTool>(
  GENERATION_TOOL,
  DEFAULT_GENERATION_TOOL
);

export const mapGenerationTool = (tool: GenerationTool): GenerationTool => {
  if (isFreestyleGenerationTool(tool) && !isCreateYourOwnTool(tool)) {
    return GENERATION_TOOL_FREESTYLE.freestyle;
  }

  return tool;
};

export const alignToolForGeneration = (tool: GenerationTool): GenerationTool => {
  // Rewrite generation tool for "freestyle" tools - should always be `freestyle`
  if (isFreestyleGenerationTool(tool)) {
    return GENERATION_TOOL.freestyle;
  }

  return tool;
};

export const alignToolForImproveFlow = (tool: GenerationTool): GenerationTool => {
  // "new image" tools should be converted to "image improve" tool in the "improve" flow
  if (isImageGenerationTool(tool) && tool !== GENERATION_TOOL.imageWithDescription) {
    return GENERATION_TOOL.imageImprove;
  }

  return tool;
};

/**
 * Try to find relevant generation tool for asset type. Notice, that there is no 1-1 relation between these two entities.
 * Some generation tools have the same asset type.
 * @param assetType - asset type
 * @param [tools] - list of generation tools to search in
 * @returns - generation tool
 */
export const getGenerationToolForAssetType = (
  assetType: AssetType,
  tools: GenerationTool[] = Object.values(GENERATION_TOOL)
): GenerationTool | null => {
  // Nine tools declare `canvas_freestyle`, so the scan below answers with whichever the enum
  // happens to list first — and a tool added to an earlier group silently takes the asset type
  // over. `freestyle` is the one the section tool selector offers, so name it here. A caller that
  // narrows `tools` gets the scan instead, because it must not receive a tool it does not offer.
  if (assetType === ASSET_TYPE.canvasFreestyle && tools.includes(GENERATION_TOOL.freestyle)) {
    return GENERATION_TOOL.freestyle;
  }

  for (let i = 0; i <= tools.length - 1; i += 1) {
    const tool = tools[i];
    const toolAssetType = getToolDefaultAssetType(tool);
    if (toolAssetType === assetType) {
      return tool;
    }
  }

  return null;
};

/**
 * Get a list of generation tools for the passed asset type.
 * @param assetType - asset type
 * @returns - list of generation tools
 */
export const getGenerationToolsByAssetType = (assetType: AssetType): GenerationTool[] => {
  const tools = Object.values(GENERATION_TOOL);
  return tools.filter((tool) => getToolDefaultAssetType(tool) === assetType);
};

export const generationToolAllowsScores = (tool: GenerationTool): boolean =>
  isDataDrivenGenerationTool(tool) || isScorableBlogTool(tool);

// A `noScore` tool is one AC never returns a score for, whatever family its name puts it in.
export const isScorableTool = (tool: GenerationTool): boolean =>
  !isNoScoreTool(tool) &&
  (isDataDrivenGenerationTool(tool) || isScorableBlogTool(tool) || isLpcoGenerationTool(tool));

/**
 * A score element must never render an empty circle. A tool that AC does not score has no number
 * to show, so it shows its name as a label. Image tools are the exception. Their score belongs to
 * the image, and the image views gate it.
 */
export const shouldShowUnscoredLabel = (tool: Maybe<GenerationTool>): boolean => {
  if (!tool) {
    return true;
  }

  return !isScorableTool(tool) && !isImageGenerationTool(tool);
};

/**
 * The label that stands in for an unscored tool's score, for example "Email text (unscored)".
 * An unknown tool has no content type to name, so it gets the bare label.
 */
export const getUnscoredLabel = (tool: Maybe<GenerationTool>): string => {
  if (!tool || isFreestyleOrImproverTool(tool) || isLongFormLocalizationTool(tool)) {
    return TEXT_UNSCORED_LABEL;
  }

  const contentType = getToolContentType(tool);
  return contentType ? `${contentType} ${TEXT_UNSCORED_LABEL.toLowerCase()}` : TEXT_UNSCORED_LABEL;
};

export const actualizeGenerationTool = (tool: GenerationTool): GenerationTool => {
  if (isDeprecatedGenerationTool(tool)) {
    return DEFAULT_GENERATION_TOOL;
  }
  return tool;
};

export const getBadgeType = (
  tool: GenerationTool,
  newTools: GenerationTool[],
  improvedTools: GenerationTool[]
): GenerationToolBadgeType | null => {
  if (improvedTools.includes(tool)) {
    return GENERATION_TOOL_BADGE_TYPE.improved;
  }
  if (newTools.includes(tool)) {
    return GENERATION_TOOL_BADGE_TYPE.new;
  }
  return null;
};

const LIST_ID_DELIMITER = ' - ';
export const formatGenerationToolListId = (
  generationTool: GenerationTool,
  toolId?: string
): string => {
  const result = [];
  if (generationTool) {
    result.push(generationTool);
  }
  result.push(LIST_ID_DELIMITER);
  if (toolId) {
    result.push(toolId);
  }
  return result.join('');
};

export const getToolFromGenerationToolListId = (listId: string): string | null =>
  listId?.split(LIST_ID_DELIMITER)[0] || null;
export const getIdFromGenerationToolListId = (listId: string): string | null =>
  listId?.split(LIST_ID_DELIMITER)[1] || null;

export const isTalkingPointsExtractionDisabledForTool = (
  generationTool: GenerationTool
): boolean => {
  const productTools: GenerationTool[] = [
    GENERATION_TOOL.basicProductReview,
    GENERATION_TOOL.generalProductListing,
  ];

  // Do not try to extract for "basic" tools
  if (isBasicGenerationTool(generationTool) && !productTools.includes(generationTool)) {
    return true;
  }

  // "annotations" can be specifically disabled to related asset type
  const assetType = getToolDefaultAssetType(generationTool);
  if (assetType && isAssetDisabledAnnotations(assetType)) {
    return true;
  }

  // Tool should support generation with annotations in order to support annotations extraction
  return !doesToolSupportSuggestKeywords(generationTool);
};

export const getFormulaTitle = (generationTool: GenerationTool): string => {
  switch (generationTool) {
    case GENERATION_TOOL.summarizer:
      return 'Summary type';
    case GENERATION_TOOL.basicContentImprover:
      return 'How to improve';
    default:
      return 'Formula';
  }
};

export const doesToolSupportImprovements = (generationTool: GenerationTool): boolean => {
  return isTextDataDrivenGenerationTool(generationTool);
};

const BLANK_PROMPT_TOOL_CONTENT: Record<string, string> = {
  [GENERATION_TOOL.generalPrompt]:
    'You are an expert marketer tasked with writing [specifics you want to focus on]',
  [GENERATION_TOOL.googleRSA]: 'Write a Google responsive search ad about [product description].',
};
export const createBlankSavedPrompt = (generationTool: GenerationTool) => {
  const entity = isAdsGenerationTool(generationTool) ? 'an ad' : 'a post';
  const emptyName = generationTool === GENERATION_TOOL.generalPrompt;
  const content = BLANK_PROMPT_TOOL_CONTENT[generationTool] || `Write ${entity} about:`;

  return {
    id: BLANK_SAVED_PROMPT_ID,
    generationTool,
    name: !emptyName ? `Write ${entity} about:` : '',
    allInstructionsData: {
      html: `<p>${content}</p>`,
    },
  };
};

// some generation tools are data driven but we still want to use the classified version of them, because the generation might be more relevant to the classified version (e.g. email_body, email_mixture, general_product_listing)
const CLASSIFIABLE_TOOLS: GenerationTool[] = [
  GENERATION_TOOL.emailBody,
  GENERATION_TOOL.emailMixture,
  GENERATION_TOOL.generalProductListing,
];
export const determineVariationGenerationTool = ({
  generationTool,
  classifiedGenerationTool,
}: {
  generationTool: GenerationTool;
  classifiedGenerationTool: GenerationTool;
}): GenerationTool => {
  if (classifiedGenerationTool) {
    if (
      !isDataDrivenGenerationTool(generationTool) ||
      CLASSIFIABLE_TOOLS.includes(generationTool)
    ) {
      return classifiedGenerationTool;
    }
  }

  return generationTool;
};

export const isPresetFreestyleToolId = (freestyleToolId: string): boolean =>
  freestyleToolId.startsWith('pp');

export const isBasicContentImproverTool = (generationTool: GenerationTool): boolean =>
  generationTool === GENERATION_TOOL.basicContentImprover;

export const isLongFormLocalizationTool = (generationTool: GenerationTool): boolean =>
  generationTool === GENERATION_TOOL.longFormLocalization;

export const isBlogWizardTemplateTool = (generationTool: GenerationTool): boolean =>
  generationTool === GENERATION_TOOL.blogDescription;

export const isGeneralTextTool = (tool: GenerationTool): boolean =>
  Object.values(GENERATION_TOOL_GENERAL_TEXT).includes(tool as GenerationToolGeneralText) ||
  Object.values(GENERATION_TOOL_BASIC).includes(tool as GenerationToolBasic) ||
  isLongFormLocalizationTool(tool);

export const isFreestyleOrImproverTool = (tool: GenerationTool): boolean =>
  isFreestyleGenerationTool(tool) || isBasicContentImproverTool(tool);

export const getToolSectionLabel = (tool: GenerationTool): string => {
  if (isFreestyleOrImproverTool(tool)) {
    return TEXT_UNSCORED_LABEL;
  }

  if (isImageGenerationTool(tool)) {
    if (tool === GENERATION_TOOL.imageByStyle) {
      return 'Image';
    }
  }

  return getToolLabel(tool);
};

export const getToolSectionDescription = (tool: GenerationTool): string =>
  lowerCase(getToolDescription(tool)).replace('body', 'full content');

export const isBlankLongFormGenerationTool = (tool: GenerationTool): boolean =>
  tool === GENERATION_TOOL.longFormBlank;

const EXCLUDED_GENERATION_TOOLS: GenerationTool[] = [
  GENERATION_TOOL.emailHeadline,
  GENERATION_TOOL.emailCta,
  GENERATION_TOOL.emailIntroParagraph,
  GENERATION_TOOL.emailSectionHeadline,
  GENERATION_TOOL.emailPromotionalBody,
  GENERATION_TOOL.emailColdOutreachSubjectLine,
  GENERATION_TOOL.emailColdOutreachBody,
  GENERATION_TOOL.articleBlogHeadline,
  GENERATION_TOOL.blogPostSectionHeadline,
  GENERATION_TOOL.blogPostSectionSubHeadline,
  GENERATION_TOOL.blogPostIntroParagraph,
  GENERATION_TOOL.emailPromotionalSubjectLine,
];

export const removeLongFormGenerationTools = (tools: GenerationTool[]): GenerationTool[] => {
  return tools.filter((tool) => !EXCLUDED_GENERATION_TOOLS.includes(tool));
};

export const isToolSubTemplatesWithinGenerationGroup = (
  generationTool: GenerationTool
): boolean => {
  const groupValues = Object.values(GENERATION_TOOLS_INCLUDING_SUB_TEMPLATES_IN_GROUP).flat();
  return groupValues.includes(generationTool);
};
